import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

import { Status as MfaStatus } from "@/store/apis/adminApi.generated";
import type { Admin, AdminSession, MfaState } from "@/store/apis/adminApi.types";

/**
 * Estado local de sesión:
 *  - Tokens y admin autenticado (persistidos en localStorage o
 *    sessionStorage según el flag `rememberMe`).
 *  - Estado del flow MFA (aal1 → aal2).
 *
 * NO cachea listados ni entidades — todo eso vive en la cache de RTK
 * Query. Este slice solo mantiene el eslabón entre el cliente y el
 * gateway (tokens + MFA).
 */

export interface AuthState {
  admin: Admin | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: string | null;
  /**
   * Estado del flujo MFA. Cuando el back devuelve `enroll_required` o
   * `challenge_required` desde sign-in, se guarda acá para gatear el
   * router hacia `/mfa`. Cuando pasa a `verified`, se limpia.
   */
  mfa: MfaState | null;
  /**
   * Flag efímero (no persistido): se enciende cuando el `baseQuery`
   * limpia la sesión por fallo de refresh — permite al `AuthGuard`
   * distinguir "nunca inició sesión" (→ /login) de "la sesión expiró
   * a media navegación" (→ /sesion-expirada). Se consume y limpia una
   * sola vez.
   */
  sessionLost: boolean;
  /**
   * "Mantener sesión en este dispositivo". Determina dónde persistir:
   *   - `true`  → `localStorage` (sobrevive al cerrar el navegador).
   *   - `false` → `sessionStorage` (se pierde al cerrar la pestaña).
   *
   * Al cargar, lo deducimos de dónde encontramos la sesión persistida.
   * Default `true` (comportamiento previo).
   */
  rememberMe: boolean;
}

const STORAGE_KEY = "arya-backoffice-session";

interface PersistedShape {
  admin: Admin | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: string | null;
}

interface LoadResult extends PersistedShape {
  rememberMe: boolean;
}

function emptyPersisted(): PersistedShape {
  return { admin: null, accessToken: null, refreshToken: null, expiresAt: null };
}

function tryParse(raw: string | null): PersistedShape | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PersistedShape>;
    return {
      admin: parsed.admin ?? null,
      accessToken: parsed.accessToken ?? null,
      refreshToken: parsed.refreshToken ?? null,
      expiresAt: parsed.expiresAt ?? null,
    };
  } catch {
    return null;
  }
}

/**
 * Carga la sesión persistida. Prueba primero `localStorage` (persistente),
 * luego `sessionStorage` (efímero). El storage donde la encontramos nos
 * dice el `rememberMe` original — así una recarga mantiene coherente la
 * decisión del usuario sin tener que re-preguntar.
 */
function loadPersisted(): LoadResult {
  if (typeof window === "undefined") {
    return { ...emptyPersisted(), rememberMe: true };
  }
  const fromLocal = tryParse(window.localStorage.getItem(STORAGE_KEY));
  if (fromLocal && fromLocal.accessToken) {
    return { ...fromLocal, rememberMe: true };
  }
  const fromSession = tryParse(window.sessionStorage.getItem(STORAGE_KEY));
  if (fromSession && fromSession.accessToken) {
    return { ...fromSession, rememberMe: false };
  }
  return { ...emptyPersisted(), rememberMe: true };
}

function persist(state: AuthState): void {
  if (typeof window === "undefined") return;
  const payload: PersistedShape = {
    admin: state.admin,
    accessToken: state.accessToken,
    refreshToken: state.refreshToken,
    expiresAt: state.expiresAt,
  };
  const str = JSON.stringify(payload);
  // Garantizamos que la sesión viva en UN solo storage — limpiamos el
  // otro para evitar que `loadPersisted` lea un valor stale si el
  // usuario cambia la decisión entre sesiones.
  if (state.rememberMe) {
    window.localStorage.setItem(STORAGE_KEY, str);
    window.sessionStorage.removeItem(STORAGE_KEY);
  } else {
    window.sessionStorage.setItem(STORAGE_KEY, str);
    window.localStorage.removeItem(STORAGE_KEY);
  }
}

function clearPersisted(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
  window.sessionStorage.removeItem(STORAGE_KEY);
}

const loaded = loadPersisted();
const initialState: AuthState = {
  admin: loaded.admin,
  accessToken: loaded.accessToken,
  refreshToken: loaded.refreshToken,
  expiresAt: loaded.expiresAt,
  mfa: null,
  sessionLost: false,
  rememberMe: loaded.rememberMe,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setSession: (state, action: PayloadAction<AdminSession>) => {
      state.accessToken = action.payload.access_token;
      state.refreshToken = action.payload.refresh_token;
      state.expiresAt = action.payload.expires_at;
      state.admin = action.payload.admin;
      state.mfa = action.payload.mfa;
      state.sessionLost = false;
      persist(state);
    },
    updateTokens: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string; expiresAt: string }>,
    ) => {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.expiresAt = action.payload.expiresAt;
      persist(state);
    },
    setMfaVerified: (state) => {
      if (state.mfa) state.mfa = { status: MfaStatus.Verified };
    },
    clearSession: (state) => {
      state.admin = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.expiresAt = null;
      state.mfa = null;
      clearPersisted();
    },
    /**
     * Dispatcheada por `baseQuery` cuando el refresh falla a media sesión.
     * Equivalente a `clearSession` + marcar la bandera `sessionLost`.
     * Separada por intención semántica: "la sesión te expiró, no fue tu
     * decisión" vs "cerraste sesión voluntariamente".
     */
    sessionExpired: (state) => {
      state.admin = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.expiresAt = null;
      state.mfa = null;
      state.sessionLost = true;
      clearPersisted();
    },
    /** Consumida por SessionExpiredPage al montar — one-shot. */
    consumeSessionLost: (state) => {
      state.sessionLost = false;
    },
    /**
     * Dispatcheada por `LoginPage` justo antes del `setSession` según
     * el checkbox "Mantener sesión en este dispositivo". No persiste
     * sola — el próximo `setSession`/`updateTokens` escribirá al
     * storage correcto.
     */
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.rememberMe = action.payload;
    },
  },
});

export const {
  setSession,
  updateTokens,
  setMfaVerified,
  clearSession,
  sessionExpired,
  consumeSessionLost,
  setRememberMe,
} = authSlice.actions;

export default authSlice.reducer;
